import axios from 'axios';
import { GLOBAL_URL, LAUNCH_PLATFORM_URL, get_headers, get_token } from '@/utils/system_constants/constant';
import { PLATFORM_IDS, get_platform_config, platform_id_for_name } from './platforms_config';
import { is_super_admin, safe_redirect_url } from './platform_switcher_helpers';

const PLATFORM_ACCESS_URL = `${GLOBAL_URL}/system_management/api/user_access/`;
const ALWAYS_VISIBLE_PLATFORM_IDS = ['aic_one'];

function enabled_platform_names(payload) {
  return (payload?.data || [])
    .filter((record) => String(record.status || '').toUpperCase() === 'ENABLED')
    .map((record) => record.platform_name)
    .filter(Boolean);
}

async function fetch_platform_access_payload() {
  // @codacy-disable-next-line
  try {
    const response = await axios.get(PLATFORM_ACCESS_URL, {
      headers: get_headers(),
      // This endpoint proxies AIC ONE. Its 401 does not mean the local Proba
      // session has expired, so it must not open the global session modal.
      skipSessionExpiryHandler: true,
    });
    if (response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Unable to load platform access');
    }
    return response.data;
  } catch (error) {
    const access_error = new Error(
      error.response?.data?.message || error.message || 'Unable to load platform access'
    );
    access_error.status = error.response?.status;
    throw access_error;
  }
}

export async function fetch_accessible_platforms(current_platform_id) {
  let platform_ids = [];
  if (is_super_admin()) {
    platform_ids = [...PLATFORM_IDS];
  } else if (get_token()) {
    const payload = await fetch_platform_access_payload();
    platform_ids = enabled_platform_names(payload)
      .map((name) => platform_id_for_name(name))
      .filter(Boolean);
  }

  platform_ids = Array.from(new Set([
    ...platform_ids,
    ...ALWAYS_VISIBLE_PLATFORM_IDS,
    current_platform_id,
  ]));

  return platform_ids.map((id) => get_platform_config(id)).filter(Boolean);
}

export async function launch_platform(platform) {
  if (!get_token()) throw new Error('Not authenticated');
  if (!platform.app_code) {
    window.location.assign(platform.launch_path || '/');
    return;
  }

  try {
    const response = await axios.post(
      LAUNCH_PLATFORM_URL,
      {
        platform: platform.app_code,
        external_user_id: sessionStorage.getItem('user_id') || sessionStorage.getItem('current_user_id'),
      },
      {
        headers: get_headers(),
        // A launch is also proxied to AIC ONE; surface its authorization error
        // in the switcher instead of treating it as local session expiry.
        skipSessionExpiryHandler: true,
      }
    );
    const redirect_url = safe_redirect_url(response.data?.redirect_url);
    if (!redirect_url) throw new Error('Platform launch URL is not approved');
    if (platform.opens_in_new_tab) window.open(redirect_url, '_blank', 'noopener');
    else window.location.assign(redirect_url);
  } catch (error) {
    const server_message = error.response?.data?.error
      || error.response?.data?.detail
      || error.response?.data?.message;
    throw new Error(server_message || error.message || 'Unable to launch platform');
  }
}
